import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useIsMobile } from '@/hooks/useIsMobile';
import { MBlock, MChips, MEmpty, MHero, MIcon, MNote, MRow, MSection, MTitle } from '@/components/mobile/MobileKit';

import type { FeatureKey } from '@/api/auth';
import { IS_APP_BUILD } from '@/lib/appBrand';
import { breakdownApi, levelIsKnown, trainersApi, type BreakdownType, type CEFRProgress } from '@/api/trainers';
import { mockApi, TYPE_LABEL_KEY } from '@/api/ieltsMock';
import { trainerScheduleApi, type StudentTrainerSchedule } from '@/api/trainerSchedule';
import { usersApi } from '@/api/users';
import { progressApi } from '@/api/progress';
import { useFeatureCheck, useHasFeature } from '@/hooks/useHasFeature';
import { useAppConfig } from '@/hooks/useAppConfig';
import { useAiSkills } from '@/hooks/useAiSkills';
import { aiTrainersShown, entryHidden, withoutClosed } from '@/lib/aiEntries';
import type { AiSkill } from '@/lib/aiSkills';
import { Card } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';
import { StudentTopBar } from '@/components/layout/StudentTopBar';
import { TrainersSidebar } from '../desktop/TrainersSidebar';

/**
 * AI Trainers hub (`/app/trainers`) — the single section that gathers every
 * trainer (Edvibe-style consolidation). Trainers are gated by tariff feature
 * inside the hub (locked overlay instead of hiding), and scoped by the selected
 * program. The CEFR tracker (phase C) and the adaptive "Boost CEFR" drill
 * (phase D) get their live data later; here they are first-class cards.
 */

/**
 * Какому виду расписания подчиняется карточка.
 *
 * ⚠️ РАСПИСАНИЕ ЕСТЬ НЕ У ВСЕХ ТРЕНАЖЁРОВ. Заказчик привязал к дням только
 * дорогие навыки и спринт: «в неделю у нас может быть 3 раза спикинг, 3 раза
 * райтинг, 3 раза спринт» (07.09.2026). Чтение и аудирование открыты всегда —
 * им расписание не задаётся, и замок на них означал бы запрет без причины.
 */
const SCHEDULE_OF: Record<string, string> = {
  '/app/ielts/sprint': 'sprint',
  '/app/trainers/ielts/writing': 'writing',
  '/app/trainers/ielts/speaking': 'speaking',
};

type Program = 'ielts' | 'general' | 'pre_upper';

// Only IELTS is offered for now (General English / Pre-Upper hidden per request).
const PROGRAMS: Program[] = ['ielts'];
const PROGRAM_KEY = 'onebilim.trainers.program';

type TrainerCard = {
  to: string;
  titleKey: string;
  descKey: string;
  icon: string;
  feature?: FeatureKey | FeatureKey[];
  /** Programs this trainer belongs to; undefined = all programs. */
  programs?: Program[];
  /** Locked until the student has completed the IELTS mock. */
  requiresMock?: boolean;
};

// The 4 IELTS skill trainers — each fills in the per-type diagnostic the mock
// doesn't fully cover. Speaking is the live realtime examiner.
const IELTS_SPRINT: TrainerCard = {
  to: '/app/ielts/sprint',
  titleKey: 'trainersHub.ieltsSprint',
  descKey: 'trainersHub.ieltsSprintDesc',
  icon: 'exercise',
  // ⚠️ Свой тумблер, а не общий `ielts`. До 04.09.2026 спринт не был закрыт
  // НИЧЕМ — ни в тарифе, ни на сервере, — и заказчик не мог убрать его,
  // например, у Pre-Upper, где спринта быть не должно вовсе.
  feature: 'sprint',
  requiresMock: true,
};
// Карточки навыков ведут в ТРЕНИРОВКУ, а не в список тестов: нажатие сразу
// открывает задание по слабому или неизвестному типу. Прежние страницы
// /app/ielts/<навык> остались доступны как каталог полных тестов — они нужны,
// когда ученик хочет пройти экзаменационный формат целиком.
const IELTS_TRAINERS: TrainerCard[] = [
  // ⚠️ У КАЖДОГО НАВЫКА СВОЙ КЛЮЧ (04.09.2026, требование заказчика).
  // Раньше чтение и слушание сидели под общей `ielts`, а письмо и речь — под
  // ключами соседних разделов (`writing_ai`, `speaking_ai`). Из-за этого нельзя
  // было собрать «доступны только чтение и слушание»: общий ключ открывал всё
  // разом, а без него не открывалось ничего.
  { to: '/app/trainers/ielts/listening', titleKey: 'trainersHub.ieltsListening', descKey: 'trainersHub.ieltsListeningDesc', icon: 'headphones', feature: 'ielts_listening', requiresMock: true },
  { to: '/app/trainers/ielts/reading', titleKey: 'trainersHub.ieltsReading', descKey: 'trainersHub.ieltsReadingDesc', icon: 'menu_book', feature: 'ielts_reading', requiresMock: true },
  { to: '/app/trainers/ielts/writing', titleKey: 'trainersHub.ieltsWriting', descKey: 'trainersHub.ieltsWritingDesc', icon: 'edit_note', feature: 'ielts_writing', requiresMock: true },
  { to: '/app/trainers/ielts/speaking', titleKey: 'trainersHub.ieltsSpeaking', descKey: 'trainersHub.ieltsSpeakingDesc', icon: 'mic', feature: 'ielts_speaking', requiresMock: true },
];

// Non-IELTS programs keep their own trainers (CEFR world).
// ⚠️ В приложении их нет (26.09.2026, сборка 61): разговора и экзамена CEFR там нет вовсе, маршруты
// `/app/speaking` и `/app/cefr` в приложении не регистрируются — карточка вела бы на 404.
const OTHER_TRAINERS: TrainerCard[] = IS_APP_BUILD
  ? []
  : [
      { to: '/app/speaking', titleKey: 'trainersHub.aiSpeaking', descKey: 'trainersHub.aiSpeakingDesc', icon: 'record_voice_over', feature: ['speaking_ai', 'speaking_peers'] },
      { to: '/app/cefr', titleKey: 'trainersHub.cefrExam', descKey: 'trainersHub.cefrExamDesc', icon: 'quiz', feature: 'cefr' },
    ];

export function TrainersHubPage() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const user = useAuthStore((st) => st.user);
  const navigate = useNavigate();
  const [program, setProgram] = useState<Program>(() => (localStorage.getItem(PROGRAM_KEY) as Program) || 'ielts');
  const pick = (p: Program) => {
    setProgram(p);
    localStorage.setItem(PROGRAM_KEY, p);
  };

  const cefrQ = useQuery({ queryKey: ['trainers', 'cefr-progress'], queryFn: trainersApi.getCefrProgress });
  const profileQ = useQuery({ queryKey: ['ielts', 'profile'], queryFn: mockApi.profile });
  // Тот же ключ, что на главной, — второго запроса не будет.
  const streakQ = useQuery({ queryKey: ['student', 'stats'], queryFn: progressApi.getMyStats });
  // Нужны календарю и заданиям в правой колонке. Ключ тот же, что в «Учёбе», —
  // второго запроса не будет.
  const marathonsQ = useQuery({
    queryKey: ['student', 'enrolled-marathons'],
    queryFn: () => usersApi.myEnrolledMarathons(),
  });
  const marathonIds = (marathonsQ.data ?? []).map((m) => m.id);

  /**
   * ⚠️ ЗАМОК РАСПИСАНИЯ ВИДЕН СРАЗУ, А НЕ ПОСЛЕ НАЖАТИЯ. Сервер отказывает на
   * входе в тренажёр, и до 07.09.2026 ученик узнавал об этом, только ткнув в
   * карточку и получив ошибку. Заказчик сказал прямо: должны «висеть замочки».
   *
   * Правило одно и то же и здесь, и на сервере (shared/trainerschedule): нет
   * расписания — закрыто. Разъедься они — календарь покажет «сегодня спикинг», а
   * тренажёр на входе откажет.
   */
  const scheduleQ = useQuery({
    queryKey: ['student', 'trainer-schedule'],
    queryFn: trainerScheduleApi.mine,
  });
  const schedule = scheduleQ.data;
  const mockDone = !!profileQ.data?.mock_done;
  // ⚠️ Мок и спринт до 04.09.2026 показывались ВСЕГДА: и строкой на телефоне, и
  // плиткой на широком экране, и оранжевым блоком «начни отсюда». Тариф на них
  // не влиял никак, хотя у Pre-Upper спринта нет вовсе.
  const can = useFeatureCheck();
  // Тумблеры навыков и мок как событие с датой (12–13.10.2026,
  // docs/CONTRACTS/ai-skills.md). Мок виден, если ему есть из чего собраться
  // (`mock_skills`). Плитка тренажёра открыта, только если её навык включён И
  // сдан в моке; включённый, но не сданный — под замком, текст — по состоянию
  // мока: окна нет — «пробный экзамен ещё не назначен», окно есть — «сначала мок».
  const aiSkills = useAiSkills();
  const hasMock = can('mock') && aiSkills.mock_skills.length > 0;
  const hasSprint = can('sprint');
  const cardOpen = (c: TrainerCard) =>
    c.to === IELTS_SPRINT.to ? aiSkills.sprint_skills.length > 0 : aiSkills[c.to.split('/').pop() as AiSkill] === true;
  const lockKey = aiSkills.mock_state === 'not_scheduled' ? 'trainersHub.mockNotScheduled' : 'trainersHub.mockRequired';
  // Мини-диагностика: мок сдан, но в ближайшем есть навык, включённый позже, — это
  // не «Пересдать», а «Сдайте mock test».
  const mockMini =
    mockDone && aiSkills.mock_state !== 'passed' && aiSkills.mock_skills.some((k) => !aiSkills[k as AiSkill]);
  const mockMeta = () => {
    if (aiSkills.mock_state === 'not_scheduled') return t('trainersHub.mockNotScheduled');
    if (aiSkills.mock_state === 'passed') return t('trainersHub.mockDone', { level: profileQ.data?.level });
    if (mockMini) return t('trainersHub.mockRequired');
    return mockDone ? t('trainersHub.mockRetake') : t('trainersHub.mockNotDone');
  };
  const isIelts = program === 'ielts';

  /**
   * ⚠️ ЗАКРЫТЫЕ ВХОДЫ УХОДЯТ ЗДЕСЬ, ОДИН РАЗ, ДО ЛЮБОЙ РАЗМЕТКИ (D22, 11.09.2026).
   * Свободный AI speaking спрятан: карточек нет ни на телефоне, ни на
   * компьютере, и верхний призыв не может указать на речь как на «слабый навык».
   * Дальше по файлу исходные списки не используются — сторож
   * `aiEntriesGuard.test.ts` считает их упоминания.
   */
  useAppConfig();
  const ieltsTrainers = withoutClosed(IELTS_TRAINERS);
  const otherTrainers = withoutClosed(OTHER_TRAINERS);
  const otherShown = otherTrainers.filter((c) => !c.programs || c.programs.includes(program));
  // ИИ-тренажёры и спринт закрыты (D26, 11.09.2026): вместо навыков — заглушка
  // «скоро вернутся», спринт не рекламируется нигде в хабе. Mock остаётся.
  const trainersShown = aiTrainersShown();
  const sprintShown = hasSprint && !entryHidden(IELTS_SPRINT.to);
  // Пришёл по закрытому адресу — объясняем, где искать.
  const [searchParams] = useSearchParams();
  const closed = searchParams.get('closed');

  // ── Телефон: макет 23.08.2026 ────────────────────────────────────────────
  // Сверху строка мок-теста (состояние, а не действие — потому синяя), затем
  // ЕДИНСТВЕННЫЙ оранжевый блок «спринт», ниже навыки одним списком.
  if (isMobile) {
    // ⚠️ Правила доступа те же, что на десктопе, а не «упрощённые для телефона»:
    // тренажёр под замком до мок-теста и тариф, закрывающий раздел. Первая
    // версия мобильной ветки их потеряла, и карточки вели туда, куда ученику
    // ещё нельзя (замечание заказчика 23.08.2026).
    const shown = (program === 'ielts' ? ieltsTrainers : otherTrainers).filter(
      (c) => !c.programs || c.programs.includes(program),
    );
    const lockedBecauseMock = (c: TrainerCard) => Boolean(c.requiresMock) && !cardOpen(c);
    return (
      <div className="space-y-3.5">
        <MTitle title={t('trainersHub.title')} subtitle={t('trainersHub.subtitle')} />

        <ClosedNotice entry={closed} hasMock={hasMock} />

        {/* Переключатель программы — тоже пропадал. Прячем только если
            программа одна: тогда выбирать не из чего. */}
        {PROGRAMS.length > 1 && (
          <MChips
            chips={PROGRAMS.map((p2) => ({ key: p2, label: t(`trainersHub.program.${p2}`) }))}
            active={program}
            onChange={(k) => pick(k as Program)}
          />
        )}

        {hasMock && (
          <MNote
            icon="fact_check"
            title={t('trainersHub.mockTitle')}
            meta={mockMeta()}
            to="/app/ielts/mock"
          />
        )}

        {sprintShown && (
          <MHero
            mascot="thinking"
            eyebrow={t('trainersHub.recommended')}
            title={t(IELTS_SPRINT.titleKey)}
            subtitle={cardOpen(IELTS_SPRINT) ? t(IELTS_SPRINT.descKey) : t(lockKey)}
            action={cardOpen(IELTS_SPRINT) ? t('trainersHub.start') : t('trainersHub.mockTitle')}
            to={cardOpen(IELTS_SPRINT) || !hasMock ? IELTS_SPRINT.to : '/app/ielts/mock'}
          />
        )}

        {isIelts && !trainersShown ? (
          <MEmpty icon="construction" title={t('trainersHub.trainersSoon')} hint={t('trainersHub.trainersSoonHint')} />
        ) : (
          <>
            <MSection title={t('trainersHub.bySkill')} right={t('trainersHub.autoCheck')} />
            <MBlock>
              {shown.map((c, i2) => (
                <MobileTrainerRow
                  key={c.to}
                  card={c}
                  tone={i2 % 2 === 0 ? 'info' : 'warm'}
                  mockLocked={lockedBecauseMock(c)}
                  lockKey={lockKey}
                  last={i2 === shown.length - 1}
                  onMock={() => navigate('/app/ielts/mock')}
                />
              ))}
            </MBlock>
          </>
        )}

        {/* ⚠️ УРОВЕНЬ ПОКАЗЫВАЕМ, ТОЛЬКО ЕСЛИ ОН ИЗМЕРЕН.
            Пока измерений нет, сервер отдаёт «A1» — это начальное значение
            переменной, а не вывод об ученике. Здесь оно превращалось в
            «Ваш уровень примерно A1» и читалось как приговор: на 07.09.2026
            уровень был измерен у 9 учеников из 1167. Заказчик в тот же день:
            «здесь у ученика бейдж A1, он её смущает».
            Вместо выдумки — понятный следующий шаг. */}
        {cefrQ.data && (
          <MNote
            icon="timer"
            title={
              levelIsKnown(cefrQ.data)
                ? t('trainersHub.cefrLevel', { level: cefrQ.data.overall_level })
                : t('trainersHub.cefrUnknown')
            }
            meta={
              levelIsKnown(cefrQ.data) ? t('trainersHub.cefrHint') : t('trainersHub.cefrUnknownHint')
            }
          />
        )}

        {/* ⚠️ РАЗБОР ПО НАВЫКАМ НУЖЕН И НА ТЕЛЕФОНЕ. Заказчик 07.09.2026: «на
            телефоне нет этого виджета». Восемь из десяти учеников заходят с
            телефона — держать разбор только на компьютере значит прятать его
            от большинства. Компонент один и тот же, отдельной мобильной
            копии нет: копия разошлась бы с настольной на первой же правке. */}
        <SkillBalance t={t} />
      </div>
    );
  }

  // Самый слабый навык по результату мока — им открывается экран.
  const bands = profileQ.data?.per_skill_band ?? {};
  const weakest = (Object.entries(bands) as Array<[string, number]>)
    .sort((a, b) => a[1] - b[1])[0];
  // ⚠️ Только среди ДОСТУПНЫХ тренажёров: самым слабым навыком вполне может
  // оказаться письмо, которого у ученика в тарифе нет, — и главный призыв
  // экрана вёл бы в закрытую дверь.
  // И только среди открытых тумблерами и сданных в моке (13.10.2026).
  const openTrainers = ieltsTrainers.filter((c) => can(c.feature as FeatureKey) && cardOpen(c));
  const weakestCard = weakest && openTrainers.find((c) => c.to.endsWith(`/${weakest[0]}`));
  // Куда ведёт оранжевый блок, если пробного экзамена в тарифе нет.
  const firstOpen = openTrainers[0]?.to ?? (sprintShown ? IELTS_SPRINT.to : '/app/lessons');

  return (
    <div>
      <StudentTopBar
        title={t('trainersHub.title')}
        subtitle={t('trainersHub.subtitle')}
        streak={streakQ.data?.current_streak ?? 0}
        name={user?.full_name}
        avatarUrl={user?.avatar_url}
      />

      <div className={cn('mb-5 inline-flex rounded-full bg-surface-container p-1', PROGRAMS.length <= 1 && 'hidden')}>
        {PROGRAMS.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => pick(p)}
            className={cn(
              'rounded-full px-5 py-[9px] text-[12.5px] font-semibold transition',
              program === p ? 'bg-primary text-on-primary' : 'text-on-surface-variant hover:bg-surface-container-high',
            )}
          >
            {t(`trainersHub.program.${p}`)}
          </button>
        ))}
      </div>

      <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div>
          <ClosedNotice entry={closed} hasMock={hasMock} className="mb-4" />

          {/* ── Что делать прямо сейчас ─────────────────────────────────── */}
          {profileQ.isLoading ? (
            <div className="h-[150px] animate-pulse rounded-[24px] bg-surface-container" />
          ) : (
            <div className="flex items-center gap-5 rounded-[24px] bg-primary px-[26px] py-[22px]">
              <div className="min-w-0 flex-1">
                <div className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-on-primary/85">
                  {mockDone ? t('trainersHub.pickedForYou') : t('trainersHub.startHere')}
                </div>
                <div className="my-[10px] mb-1.5 font-display text-[26px] font-bold leading-[1.1] text-on-primary">
                  {mockDone && weakestCard ? t(weakestCard.titleKey) : t('trainersHub.mockCard')}
                </div>
                <p className="text-[13px] text-on-primary/90">
                  {mockDone && weakestCard
                    ? t('trainersHub.weakestHint', { band: weakest![1].toFixed(1) })
                    : // Тренажёры закрыты (D26) — «откроем тренажёры» было бы неправдой.
                      trainersShown ? t('trainersHub.diagnosticHint') : t('trainersHub.diagnosticHintTrainersClosed')}
                </p>
                {mockDone && weakestCard && (
                  <p className="mt-1.5 text-[11px] text-on-primary/80">{t('trainersHub.approxBandNote')}</p>
                )}
              </div>
              <Link
                to={mockDone && weakestCard ? weakestCard.to : hasMock ? '/app/ielts/diagnostic' : firstOpen}
                className="inline-flex flex-none items-center gap-2 rounded-full bg-white px-[22px] py-[13px] text-[14.5px] font-bold text-primary-pressed transition hover:bg-primary-container"
              >
                {mockDone ? t('trainersHub.start') : t('trainersHub.diagnosticCta')}
                <span className="material-symbols-outlined text-[17px]">arrow_forward</span>
              </Link>
            </div>
          )}

          {/* Свод по уровню CEFR — только для программ без IELTS: там уровень
              считается по занятиям, а не по моку. Сейчас такая программа не
              включена, но выбрасывать готовый блок ради вида незачем. */}
          {!isIelts && <CEFRTracker data={cefrQ.data} loading={cefrQ.isLoading} t={t} />}

          {/* ── Все тренажёры ───────────────────────────────────────────── */}
          <h2 className="mx-0.5 mb-[11px] mt-[22px] font-display text-[15.5px] font-bold text-on-surface">
            {t('trainersHub.allTrainers')}
          </h2>
          <div className="grid grid-cols-2 gap-3">
            {/* Закрытые ИИ-тренажёры — одна плитка-заглушка рядом с моком. */}
            {isIelts && !trainersShown && (
              <div className="rounded-[20px] border-2 border-dashed border-outline px-[18px] py-4">
                <div className="mb-5 flex items-center">
                  <span className="grid h-9 w-9 place-items-center rounded-xl bg-surface-container-low">
                    <span className="material-symbols-outlined text-[19px] text-on-surface-variant">construction</span>
                  </span>
                </div>
                <div className="text-[14px] font-bold text-on-surface">{t('trainersHub.trainersSoon')}</div>
                <div className="mt-[3px] text-[12.5px] text-on-surface-variant">{t('trainersHub.trainersSoonHint')}</div>
              </div>
            )}
            {(isIelts ? withoutClosed([IELTS_SPRINT, ...ieltsTrainers]) : otherShown).map((c) => (
              <TrainerTile
                  key={c.to}
                  card={c}
                  t={t}
                  mockDone={cardOpen(c)}
                  lockKey={lockKey}
                  band={bands[c.to.split('/').pop() ?? '']}
                  schedule={schedule}
                />
            ))}
            {/* Мок-тест — такой же плиткой, последней: он и состояние, и вход. */}
            {isIelts && hasMock && (
              <Link
                to="/app/ielts/diagnostic"
                className="rounded-[20px] bg-surface-container px-[18px] py-4 transition hover:bg-surface-container-high"
              >
                <div className="mb-5 flex items-center justify-between">
                  <span className="grid h-9 w-9 place-items-center rounded-xl bg-info-container">
                    <span className="material-symbols-outlined text-[19px] text-info">star</span>
                  </span>
                  {mockDone && profileQ.data?.level && (
                    <span className="rounded-full bg-info-container px-[9px] py-1 text-[10px] font-bold text-info">
                      {profileQ.data.level}
                    </span>
                  )}
                </div>
                <div className="text-[14px] font-bold text-on-surface">{t('trainersHub.mockCard')}</div>
                <div className="mt-[3px] text-[12.5px] text-on-surface-variant">
                  {aiSkills.mock_state === 'open' && !mockMini
                    ? mockDone ? t('trainersHub.mockRetake') : t('trainersHub.mockCardDesc')
                    : mockMeta()}
                </div>
              </Link>
            )}
          </div>

          {/* ⚠️ СИЛЬНЫЕ И СЛАБЫЕ СТОРОНЫ — ПОД СЕТКОЙ, А НЕ СБОКУ. Сперва я
              поставил их в правую колонку, и заказчик 07.09.2026 их там просто
              не нашёл: правая колонка занята виджетами, а разбор по навыкам —
              это про сами тренажёры, и стоять он должен рядом с ними. */}
          <SkillBalance t={t} />
        </div>

        <TrainersSidebar weak={cefrQ.data?.weak} marathonIds={marathonIds} />
      </div>
    </div>
  );
}

/**
 * Объяснение для того, кто пришёл по закрытому адресу.
 *
 * ⚠️ БЕЗ НЕГО ПЕРЕНАПРАВЛЕНИЕ ВЫГЛЯДИТ ПОЛОМКОЙ. Человек нажал закладку «IELTS
 * Speaking» и оказался в списке тренажёров, где речи нет: со стороны это «у меня
 * пропал раздел». Одна строка говорит, где он теперь, и ведёт туда.
 */
function ClosedNotice({ entry, hasMock, className }: { entry: string | null; hasMock: boolean; className?: string }) {
  const { t } = useTranslation();
  if (entry !== 'speaking') return null;
  return (
    <div
      role="status"
      className={cn('flex flex-wrap items-center gap-3 rounded-[20px] bg-info-container px-[18px] py-3.5', className)}
    >
      <span className="material-symbols-outlined text-[20px] text-info">mic</span>
      <p className="min-w-0 flex-1 text-[13.5px] font-semibold text-on-surface">{t('trainersHub.speakingInMock')}</p>
      {hasMock && (
        <Link
          to="/app/ielts/mock"
          className="inline-flex min-h-11 items-center rounded-full bg-info px-4 text-[12.5px] font-bold text-white transition hover:brightness-110"
        >
          {t('trainersHub.mockTitle')}
        </Link>
      )}
    </div>
  );
}

function CEFRTracker({
  data,
  loading,
  t,
}: {
  data?: CEFRProgress;
  loading: boolean;
  t: (k: string, o?: Record<string, unknown>) => string;
}) {
  return (
    <Card className="mb-4 shadow-ambient">
      <div className="mb-3 flex items-center gap-3">
        <span className="material-symbols-outlined text-3xl text-primary">trending_up</span>
        <div className="flex-1">
          <p className="font-display text-lg font-bold tracking-display text-on-surface">{t('trainersHub.cefrTrackerTitle')}</p>
          {loading ? (
            <p className="text-sm text-on-surface-variant">…</p>
          ) : levelIsKnown(data) ? (
            <p className="text-sm text-on-surface-variant">
              <span className="font-semibold text-on-surface">{data.overall_level}</span>
              {data.overall_level !== data.next_level && (
                <> · {t('trainersHub.toNext', { next: data.next_level, points: data.points_to_next })}</>
              )}
            </p>
          ) : (
            <p className="text-sm text-on-surface-variant">{t('trainersHub.cefrUnknownHint')}</p>
          )}
        </div>
        {/* Значок с уровнем — только когда уровень настоящий. Иначе это ярлык,
            который платформа вешает на человека, ничего о нём не зная. */}
        {levelIsKnown(data) && <Pill variant="primary">{data.overall_level}</Pill>}
      </div>
      {/* Полосы по навыкам — тоже про уровень: без измерений сервер ставит
          каждому навыку «A1», и ученик видит четыре одинаковых приговора
          вместо признания, что мы его ещё не проверяли. */}
      {levelIsKnown(data) && (
        <div className="grid gap-2 sm:grid-cols-2">
          {data.skills.map((s) => (
            <div key={s.skill}>
              <div className="mb-0.5 flex items-center justify-between text-xs">
                <span className="font-semibold text-on-surface">{t(`trainersHub.skill.${s.skill}`)}</span>
                <span className="text-on-surface-variant">
                  {s.level}
                  {s.level !== s.next_level ? ` → ${s.next_level}` : ''}
                </span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-pill bg-surface-container-highest">
                <div className="h-full bg-primary" style={{ width: `${Math.max(0, Math.min(100, s.score))}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function TrainerTile({
  card,
  t,
  mockDone,
  lockKey = 'trainersHub.mockRequired',
  band,
  schedule,
}: {
  card: TrainerCard;
  t: (k: string, o?: Record<string, unknown>) => string;
  /** Навык этой плитки сдан в моке (13.10.2026 — по навыку, а не по профилю). */
  mockDone: boolean;
  /** Текст замка «сначала мок»: окна нет — «пробный экзамен ещё не назначен». */
  lockKey?: string;
  /** Балл по этому навыку из мока — показываем меткой, как в макете. */
  band?: number;
  /** Расписание тренажёров ученика. undefined — ещё грузится. */
  schedule?: StudentTrainerSchedule[];
}) {
  // useHasFeature is lockout-safe: no feature key → always unlocked.
  const keys = card.feature ? (Array.isArray(card.feature) ? card.feature : [card.feature]) : [];
  // A trainer is unlocked if it has no feature, or the student holds ANY of its
  // features. Hooks can't be called conditionally, so we always evaluate two
  // slots but only COUNT a slot that maps to a real key (the fallback 'lessons'
  // is always-true and must never leak into the decision for single-feature
  // cards — that would defeat gating).
  const has0 = useHasFeature(keys[0] ?? 'lessons');
  const has1 = useHasFeature(keys[1] ?? 'lessons');
  const featureUnlocked = keys.length === 0 || (keys[0] ? has0 : false) || (keys[1] ? has1 : false);
  // Mock gate: adaptive trainers open only after the diagnostic mock is done.
  const mockLocked = !!card.requiresMock && !mockDone;

  /**
   * ⚠️ ЗАМОК РАСПИСАНИЯ — ТРЕТЬЯ, ОТДЕЛЬНАЯ ПРИЧИНА, И ПУТАТЬ ЕЁ С ДВУМЯ
   * ДРУГИМИ НЕЛЬЗЯ:
   *
   *   тариф      → карточки нет вовсе (её не купили);
   *   мок        → «сначала сдай пробный экзамен» — дело за учеником;
   *   расписание → «куратор ещё не назначил дни» — дело за куратором.
   *
   * Сказать «сдай мок» тому, кто его сдал и ждёт куратора, — значит отправить
   * человека делать то, что он уже сделал.
   *
   * ⚠️ ПОКА РАСПИСАНИЕ ГРУЗИТСЯ, ЗАМКА НЕТ. Показать замок на секунду и убрать —
   * значит соврать: человек успеет прочитать «закрыто» и уйти.
   */
  const scheduleKind = SCHEDULE_OF[card.to];
  const rule = schedule?.find((x) => x.trainer === scheduleKind);
  const scheduleLocked = !!scheduleKind && !!schedule && !rule?.open_now;
  const nextOpen = rule?.next_open;

  // ⚠️ КАРТОЧКУ, ЗАКРЫТУЮ ТАРИФОМ, НЕ ПОКАЗЫВАЕМ ВООБЩЕ — и в браузере тоже.
  //
  // Правило заказчика 04.09.2026: «то, что недоступно в тарифах, просто
  // прячем». Раньше пряталось только в приложении из магазина, а в браузере
  // висел замок — то есть ученик Pre-Upper видел тренажёр речи, которого у
  // него нет и не будет. Замок остаётся ровно для одного случая: обучение ещё
  // не началось (см. AppShell). Замок мок-теста тоже остаётся — он про учёбу,
  // а не про деньги.
  if (!featureUnlocked) return null;

  const unlocked = featureUnlocked && !mockLocked && !scheduleLocked;

  const body = (
    <div
      className={cn(
        'h-full rounded-[20px] bg-surface-container px-[18px] py-4 transition',
        unlocked ? 'cursor-pointer hover:bg-surface-container-high' : 'opacity-70',
      )}
    >
      <div className="mb-5 flex items-center justify-between">
        <span className="grid h-9 w-9 place-items-center rounded-xl bg-primary-container">
          <span className="material-symbols-outlined text-[19px] text-primary">{card.icon}</span>
        </span>
        {!unlocked ? (
          <span className="material-symbols-outlined text-[16px] text-on-surface-variant">lock</span>
        ) : (
          band != null && (
            // Балл мока — примерный (владелец 20.10.2026): «≈» на плитке, полная подпись — подсказкой.
            <span
              title={t('trainersHub.approxBand', { band: band.toFixed(1) })}
              aria-label={t('trainersHub.approxBand', { band: band.toFixed(1) })}
              className="rounded-full bg-primary-container px-[9px] py-1 text-[10px] font-bold text-on-primary-container"
            >
              ≈ {band.toFixed(1)}
            </span>
          )
        )}
      </div>
      <div className="text-[14px] font-bold text-on-surface">{t(card.titleKey)}</div>
      <div className="mt-[3px] text-[12.5px] text-on-surface-variant">
        {mockLocked
          ? t(lockKey)
          : scheduleLocked
            ? nextOpen
              ? t('trainersHub.opensOn', {
                  date: new Date(nextOpen).toLocaleDateString(undefined, { day: 'numeric', month: 'long' }),
                })
              : t('trainersHub.noScheduleYet')
            : t(card.descKey)}
      </div>
    </div>
  );

  // When locked only by the mock gate, tapping sends the student to the mock.
  if (unlocked) return <Link to={card.to}>{body}</Link>;
  if (mockLocked && featureUnlocked) return <Link to="/app/ielts/diagnostic">{body}</Link>;
  return <div>{body}</div>;
}

/**
 * Строка тренажёра в списке на телефоне.
 *
 * ⚠️ ПРИЧИН БЛОКИРОВКИ ДВЕ, И ОНИ РАЗНЫЕ. Раньше список учитывал только
 * непройденный мок-тест и на любой замок писал «сначала сдайте мок». Для
 * тренажёра, которого просто нет в доступе, это неправда: человек сдаёт тест и
 * упирается в ту же дверь. С 24.08.2026 живой разговор и разбор эссе закрыты
 * от бесплатных учеников — таких строк стало больше, и разделять причины
 * обязательно.
 *
 * Формулировка нейтральная, без цен и без кнопки покупки: правила App Store
 * запрещают вести к оплате мимо Apple, а доступ к курсам школа выдаёт сама.
 */
function MobileTrainerRow({
  card,
  tone,
  mockLocked,
  lockKey = 'trainersHub.lockedByMock',
  last,
  onMock,
}: {
  card: TrainerCard;
  tone: 'info' | 'warm';
  mockLocked: boolean;
  /** Текст замка «сначала мок»: окна нет — «пробный экзамен ещё не назначен». */
  lockKey?: string;
  last: boolean;
  onMock: () => void;
}) {
  const { t } = useTranslation();
  const keys = card.feature ? (Array.isArray(card.feature) ? card.feature : [card.feature]) : [];
  const has0 = useHasFeature(keys[0] ?? 'lessons');
  const has1 = useHasFeature(keys[1] ?? 'lessons');
  const featureUnlocked = keys.length === 0 || (keys[0] ? has0 : false) || (keys[1] ? has1 : false);

  // Та же строка, что в настольной плитке: закрытое тарифом не показываем.
  if (!featureUnlocked) return null;

  const locked = mockLocked;

  return (
    <MRow
      leading={<MIcon icon={locked ? 'lock' : card.icon} tone={locked ? 'muted' : tone} />}
      title={t(card.titleKey)}
      // ⚠️ Ветки «Доступно ученикам школы / академии» здесь больше нет (15.09.2026, Apple 3.1.3(f)):
      // закрытое тарифом не рисуется вовсе (return null выше), ветка была недостижима, а строка
      // ехала в бандл и роняла проверку release-ios.sh.
      meta={mockLocked ? t(lockKey) : t(card.descKey)}
      metaAccent={locked}
      dim={locked}
      to={locked ? undefined : card.to}
      // На мок-тест ведём только тогда, когда дело действительно в нём.
      onClick={mockLocked && featureUnlocked ? onMock : undefined}
      last={last}
    />
  );
}

/**
 * Разбор по навыкам: раскрывающийся список, внутри — каждый тип задания.
 *
 * ⚠️ ЧТО ИМЕННО ПРОСИЛ ЗАКАЗЧИК (07.09.2026, дословно): «выпадающие списки
 * направлений reading, listening, speaking, writing. Нажимаю на reading —
 * выпадает полная детализированная оценка: headings — было вопросов столько-то,
 * ответил правильно на столько-то, слабый; true/false — столько-то, сильный;
 * тест — неизвестный навык».
 *
 * ⚠️ ПОЧЕМУ ПОЛОСЫ ПО НАВЫКАМ НЕ ПОДОШЛИ. Первые две редакции показывали
 * «чтение сильнее письма» одной строкой. Заказчик дважды сказал, что имел в
 * виду не это, и он прав: «чтение слабое» ученику некуда приложить, а «headings
 * 3 из 10» — можно, это прямое указание, что открывать следующим.
 *
 * ⚠️ НАВЫКИ ФИЛЬТРУЕТ СЕРВЕР, А НЕ ЭКРАН. Тариф — это деньги; проверять его
 * разметкой значит открыть закрытое всякому, кто зайдёт по адресу напрямую.
 */
function SkillBalance({ t }: { t: (k: string, o?: Record<string, unknown>) => string }) {
  const [open, setOpen] = useState<string | null>(null);

  const q = useQuery({ queryKey: ['trainers', 'breakdown'], queryFn: breakdownApi.get });
  const skills = q.data ?? [];
  if (skills.length === 0) return null;

  return (
    <div className="mt-3.5 rounded-[20px] bg-surface-container px-[18px] py-4">
      <h3 className="mb-1 font-display text-[14.5px] font-bold text-on-surface">
        {t('trainersHub.strengthsTitle')}
      </h3>
      <p className="mb-3 text-[12px] text-on-surface-variant">{t('trainersHub.strengthsOpenHint')}</p>

      <div className="flex flex-col">
        {skills.map((sec) => {
          const isOpen = open === sec.skill;
          const weakCount = sec.types.filter((x) => x.state === 'weak').length;
          const untriedCount = sec.types.filter((x) => x.state === 'unknown').length;

          return (
            <div key={sec.skill} className="border-b border-outline last:border-b-0">
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : sec.skill)}
                aria-expanded={isOpen}
                className="flex w-full items-center gap-3 py-3 text-left"
              >
                <span
                  className={cn(
                    'material-symbols-outlined flex-none text-[20px] text-on-surface-variant transition-transform',
                    isOpen && 'rotate-90',
                  )}
                >
                  chevron_right
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[13.5px] font-bold text-on-surface">
                    {t(`trainersHub.skill.${sec.skill}`)}
                  </span>
                  {/* Сводка строкой: ради неё список и не раскрывают лишний раз. */}
                  <span className="block text-[11.5px] text-on-surface-variant">
                    {sec.total > 0
                      ? t('trainersHub.skillSummary', {
                          correct: sec.correct,
                          total: sec.total,
                          weak: weakCount,
                          unknown: untriedCount,
                        })
                      : t('trainersHub.skillUntouched')}
                  </span>
                </span>
              </button>

              {isOpen && (
                <div className="pb-3 pl-5">
                  {sec.types.map((row) => (
                    <TypeRow key={row.type} row={row} t={t} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Названия видов, которых нет в общем словаре типов: эссе и части речи. */
const OWN_TYPE_LABEL: Record<string, string> = {
  opinion: 'Opinion',
  discussion: 'Discussion',
  advantages_disadvantages: 'Advantages / Disadvantages',
  problem_solution: 'Problem & Solution',
  two_part: 'Double Question',
  part1: 'Part 1 — Interview',
  part2: 'Part 2 — Long Turn',
  part3: 'Part 3 — Discussion',
};

const STATE_STYLE: Record<string, string> = {
  weak: 'bg-warning-container text-warning',
  strong: 'bg-success-container text-success',
  unknown: 'bg-surface-container-high text-on-surface-variant',
};

function TypeRow({
  row,
  t,
}: {
  row: BreakdownType;
  t: (k: string, o?: Record<string, unknown>) => string;
}) {
  const labelKey = TYPE_LABEL_KEY[row.type];
  const name = OWN_TYPE_LABEL[row.type] ?? (labelKey ? t(labelKey) : row.type);

  return (
    <div className="flex items-center gap-3 py-[7px]">
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[12.5px] font-semibold text-on-surface">{name}</span>
        {/* ⚠️ «БЫЛО / ВЕРНО» ЧИСЛАМИ, А НЕ ПРОЦЕНТОМ. Заказчик просил именно так,
            и это честнее: «60%» при трёх вопросах выглядит как измеренный
            результат, а «2 из 3» сразу показывает, на чём он основан. */}
        <span className="block text-[11px] text-on-surface-variant">
          {row.total > 0
            ? t('trainersHub.typeScore', { correct: row.correct, total: row.total })
            : t('trainersHub.typeNoData')}
        </span>
      </span>
      <span
        className={cn(
          'flex-none rounded-full px-2.5 py-1 text-[10.5px] font-bold',
          STATE_STYLE[row.state] ?? STATE_STYLE.unknown,
        )}
      >
        {t(`trainersHub.state.${row.state}`)}
      </span>
    </div>
  );
}
